import { useEffect } from "react";
import { Vector3 } from "three";
import {
  MeasurementCompletedEvent,
  MultiPointMeasureControlsLogic,
} from "./multi-point-measures-controls-logic";

type OnCurrentPointChanged = (point: Vector3 | undefined) => void;

type OnMeasurementCompleted = (event: MeasurementCompletedEvent) => void;

type OnPointsChanged = (points: Vector3[] | undefined) => void;

/**
 * This hook is used by the MultiPointMeasureControls component, to ensure that the signals of
 * the MultiPointMeasureControlsLogic object are properly forwarded to the given callbacks.
 *
 * @param controls The object responsible for the models' measurement logic
 * @param onCurrentPointChanged Callback issued when a picked point changes
 * @param onMeasurementCompleted Callback issued when the user completes the measurement
 * @param onPointsChanged Callback issued when the points change
 * @param onMeasurementStarted Callback issued when the measurement is started
 * @param onMeasurementCanceled Callback issued when the measurement is canceled
 * @param maxPoints Optional maximum number of points to be used in the measurement
 */
export function useAttachCallbacksToControls(
  controls: MultiPointMeasureControlsLogic,
  onCurrentPointChanged: OnCurrentPointChanged,
  onMeasurementCompleted: OnMeasurementCompleted,
  onPointsChanged?: OnPointsChanged,
  onMeasurementStarted?: () => void,
  onMeasurementCanceled?: () => void,
  maxPoints?: number,
): void {
  // Attach all callbacks
  useEffect(() => {
    const disp = controls.onMeasurementStarted.on(() =>
      onMeasurementStarted?.(),
    );
    return () => {
      disp.dispose();
    };
  }, [controls, onMeasurementStarted]);

  useEffect(() => {
    const disp = controls.onCurrentPointChanged.on((point) => {
      onCurrentPointChanged(point);
    });
    return () => {
      disp.dispose();
    };
  }, [controls, onCurrentPointChanged]);

  useEffect(() => {
    if (!onPointsChanged) return;
    const disp = controls.onPointsChanged.on(() => {
      if (maxPoints && controls.points && controls.points.length > maxPoints) {
        controls.deletePoint(controls.points.length - 1, false);
      } else {
        onPointsChanged(controls.points);
      }
    });
    return () => {
      disp.dispose();
    };
  }, [controls, maxPoints, onCurrentPointChanged, onPointsChanged]);

  useEffect(() => {
    const disp = controls.onMeasurementCompleted.on((event) => {
      onMeasurementCompleted(event);
    });
    return () => {
      disp.dispose();
    };
  }, [controls, onMeasurementCompleted]);

  useEffect(() => {
    if (!onMeasurementCanceled) return;
    const disp = controls.onMeasurementCanceled.on(() => {
      onMeasurementCanceled();
    });
    return () => {
      disp.dispose();
    };
  }, [controls, onMeasurementCanceled]);
}
